import { useState, useEffect, useRef } from 'react'
import PropTypes from 'prop-types'

import { STORE_AVAILABILITY as dict } from '@/resources/dict'
import useFeatureToggle from '@/hooks/useFeatureToggle'

import Switch from '@/components/business/Switch'
import { HexaV2Toggle } from '@/components/hexa-v2'

import REASONS_TO_CLOSE, {
  reasonsDescriptionToClose,
} from '@/resources/enums/reasonsToClose'
import { FeatureToggle } from '@/resources/enums/featureToggle'
import { useSeller } from '@/contexts/seller'
import { useSellerStatus } from '@/proxy/seller/hooks'

import AlertBox from './AlertBox'
import StoreOpeningModal from './StoreOpeningModal'
import HexaV2StoreClosingDialog from './HexaV2StoreClosingDialog'
import HexaV2StoreOpeningDialog from './HexaV2StoreOpeningDialog'
import StoreClosingModal from './StoreClosingModal'

import * as S from './styles'

import { sendMessageToPdvAlertChannel } from './slackAPI'
import { useAnalytics } from '@/hooks/useAnalytics'
import { STORE_OPEN_STATUS } from '@/resources/utils'

const StoreAvailability = ({ pollingTime = 30000, isMobile }) => {
  const [isOpen, setIsOpen] = useState(false)
  const [showSwitch, setShowSwitch] = useState(false)
  const [showOpenStoreModal, setShowOpenStoreModal] = useState(false)
  const [showCloseStoreModal, setShowCloseStoreModal] = useState(false)
  const [showCloseStoreReminder, setShowCloseStoreReminder] = useState(false)

  const { isLoadingSeller, isTrainingOrOperating } = useSeller()
  const analytics = useAnalytics()

  const intervalRef = useRef()
  const ignorePollingRef = useRef()

  const getToggleLabelText = () => {
    if (isMobile) {
      return isOpen ? dict.opened : dict.closed
    }
    return isOpen ? dict.storeOpened : dict.storeClosed
  }

  const isScheduledOrdersActive =
    useFeatureToggle({
      splitName: 'scheduled_orders_beta',
    }) === FeatureToggle.ON

  const isHexaDialogActive =
    useFeatureToggle({
      splitName: 'seller_hexa_store_availability_modal',
    }) === FeatureToggle.ON

  const isHexaHeader =
    useFeatureToggle({
      splitName: 'hexa_menu_header',
    }) === FeatureToggle.ON

  const ignorePollingTime = () => {
    ignorePollingRef.current = true
    clearInterval(intervalRef.current)

    intervalRef.current = setInterval(() => {
      ignorePollingRef.current = false
      clearInterval(intervalRef.current)
      intervalRef.current = null
    }, pollingTime)
  }

  const onClickStatusButton = ({ target: { checked } }) => {
    setShowCloseStoreModal(!checked)
    setShowOpenStoreModal(checked)
  }

  const onClickHexaToggleButton = target => {
    setShowCloseStoreModal(target)
    setShowOpenStoreModal(!target)
  }

  const onClickCloseModal = () => {
    setShowCloseStoreModal(false)
    setShowOpenStoreModal(false)
  }

  const handleSuccessfulOpening = () => {
    ignorePollingTime()
    setShowOpenStoreModal(false)
    setIsOpen(true)
    localStorage.setItem(STORE_OPEN_STATUS, 'true')
    analytics('Store Status Changed')
  }

  const handleCallToSlackAPI = async ({
    pocInfo,
    selectedReason,
    otherReasons,
  }) => {
    const {
      id,
      ownerName,
      address: { city, provinceCode },
      phoneNumbers = [],
      isTimeInsideWorking = false,
    } = pocInfo

    const description = reasonsDescriptionToClose[selectedReason]
    const partnerPhone = phoneNumbers
      .map(({ phoneNumber }) => phoneNumber)
      .join(',')
    const isOtherReason = selectedReason === REASONS_TO_CLOSE.OTHER

    const sendMessageFnArgs = {
      partnerID: id,
      partnerName: ownerName,
      partnerPhone,
      pdvStateCode: provinceCode,
      pdvCity: city,
      reason: isOtherReason ? `${description} - ${otherReasons}` : description,
    }

    if (isTimeInsideWorking) {
      sendMessageToPdvAlertChannel(sendMessageFnArgs)
    }
  }

  const handleSuccessfulClosing = ({ poc, selectedReason, otherReasons }) => {
    ignorePollingTime()

    setIsOpen(false)
    setShowCloseStoreModal(false)
    setShowCloseStoreReminder(false)
    localStorage.setItem(STORE_OPEN_STATUS, 'false')

    handleCallToSlackAPI({ pocInfo: poc, selectedReason, otherReasons })

    analytics('Store Status Changed')
  }

  const { isLoading = false, data: { seller = {} } = {} } = useSellerStatus({
    refetchInterval: pollingTime,
  })

  useEffect(() => {
    if (!seller || isLoading) return

    const { id, shop = {} } = seller

    if (!sessionStorage.getItem('pocId')) {
      sessionStorage.setItem('pocId', id)
    }

    if (shop.isOpen !== undefined) {
      if (ignorePollingRef?.current) return
      localStorage.setItem(STORE_OPEN_STATUS, String(shop.isOpen))

      if (shop.isOpen !== isOpen) {
        setIsOpen(shop.isOpen)
      }

      if (
        !shop.isTimeInsideWorking &&
        !showCloseStoreReminder &&
        shop.isOpen &&
        !isScheduledOrdersActive
      ) {
        setShowCloseStoreReminder(true)
      }

      setShowSwitch(true)
    }
  }, [
    isLoading,
    seller,
    isOpen,
    showCloseStoreReminder,
    ignorePollingRef,
    isScheduledOrdersActive,
  ])

  return (
    isTrainingOrOperating &&
    !isLoadingSeller && (
      <>
        <>
          {isHexaHeader && showSwitch ? (
            <HexaV2Toggle
              data-testid="hexa-toggle-store-availability"
              selected={isOpen}
              label={getToggleLabelText()}
              onHxClick={({ target }) => {
                onClickHexaToggleButton(target.selected)
              }}
              disabled={isScheduledOrdersActive}
            />
          ) : (
            <S.AdminStatus isHexaHeader={isHexaHeader}>
              {showSwitch && (
                <Switch
                  checked={isOpen}
                  value={isOpen}
                  index={isOpen}
                  openLabel={dict.openedPoc}
                  closeLabel={dict.closedPoc}
                  onChange={onClickStatusButton}
                  disabled={isScheduledOrdersActive}
                  tooltip={{
                    text: dict.changeAvailabilityBlock,
                    disabled: !isScheduledOrdersActive,
                    placement: 'bottomLeft',
                  }}
                />
              )}
            </S.AdminStatus>
          )}
          {showCloseStoreReminder && (
            <AlertBox
              id="alert-close-store"
              message={dict.closeStoreReminderText}
              isHexaHeader={isHexaHeader}
            />
          )}
        </>

        {showCloseStoreModal &&
          (isHexaDialogActive ? (
            <HexaV2StoreClosingDialog
              handleCloseModal={onClickCloseModal}
              handleSuccessfulClosing={handleSuccessfulClosing}
              show={showCloseStoreModal}
            />
          ) : (
            <StoreClosingModal
              handleCloseModal={onClickCloseModal}
              handleSuccessfulClosing={handleSuccessfulClosing}
              show={showCloseStoreModal}
            />
          ))}

        {showOpenStoreModal &&
          (isHexaDialogActive ? (
            <HexaV2StoreOpeningDialog
              handleCloseModal={onClickCloseModal}
              handleSuccessfulOpening={handleSuccessfulOpening}
              show={showOpenStoreModal}
            />
          ) : (
            <StoreOpeningModal
              handleCloseModal={onClickCloseModal}
              handleSuccessfulOpening={handleSuccessfulOpening}
              show={showOpenStoreModal}
            />
          ))}
      </>
    )
  )
}

StoreAvailability.propTypes = {
  pollingTime: PropTypes.number,
  isMobile: PropTypes.bool,
}

export default StoreAvailability
